import type { PromoLibraryKpiFormat } from '../../config/promoLibraryConfig';

export const EMPTY_VALUE = '--';

/**
 * `Intl.NumberFormat` construction is expensive relative to `format()`, and the
 * grid formats one per cell across thousands of cells, so instances are cached
 * by their full option set for the lifetime of the module.
 */
const formatterCache = new Map<string, Intl.NumberFormat>();

const getFormatter = (format: PromoLibraryKpiFormat, precision: number, currencyCode: string): Intl.NumberFormat => {
    const cacheKey = `${format}|${precision}|${currencyCode}`;
    const cached = formatterCache.get(cacheKey);
    if (cached) return cached;

    const options: Intl.NumberFormatOptions =
        format === 'currency'
            ? {
                  style: 'currency',
                  currency: currencyCode,
                  minimumFractionDigits: precision,
                  maximumFractionDigits: precision,
              }
            : { minimumFractionDigits: precision, maximumFractionDigits: precision };

    // An unsupported currency code throws; fall back to a plain number so a bad
    // config value degrades the cell instead of blanking the grid.
    let formatter: Intl.NumberFormat;
    try {
        formatter = new Intl.NumberFormat('en-US', options);
    } catch {
        formatter = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
        });
    }

    formatterCache.set(cacheKey, formatter);
    return formatter;
};

const toNumber = (value: number | string | boolean | null | undefined): number | null => {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

/** Formats a KPI value per its column config. Non-numeric input renders as text. */
export function formatKpiValue(
    value: number | string | boolean | null | undefined,
    format: PromoLibraryKpiFormat,
    precision = 2,
    currencyCode = 'USD',
): string {
    // Matches the edit modal's True/False wording so the cell and the dialog
    // describe a boolean KPI the same way.
    if (typeof value === 'boolean') return value ? 'True' : 'False';

    if (format === 'text') {
        return value === null || value === undefined || value === '' ? EMPTY_VALUE : String(value);
    }

    const numeric = toNumber(value);
    if (numeric === null) return EMPTY_VALUE;

    const formatted = getFormatter(format, precision, currencyCode).format(numeric);
    return format === 'percent' ? `${formatted}%` : formatted;
}

/**
 * Percentage delta between the baseline and the current value. Null whenever the
 * change is undefined rather than zero: a missing side, or a zero baseline (where
 * the percentage is infinite and would read as a bogus spike).
 */
export function computeChangePercent(
    current: number | string | boolean | null | undefined,
    previous: number | string | boolean | null | undefined,
): number | null {
    const currentValue = toNumber(current);
    const previousValue = toNumber(previous);
    if (currentValue === null || previousValue === null || previousValue === 0) return null;
    return ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
}

/** Substitutes `{loaded}` / `{total}` in the configured record-count label. */
export function formatRecordCount(template: string, loaded: number, total: number): string {
    const countFormatter = getFormatter('number', 0, '');
    return template
        .replace('{loaded}', countFormatter.format(loaded))
        .replace('{total}', countFormatter.format(total));
}
